<template>
  <div :style="{ position: 'relative' }">
    <v-overlay :value="isLoading" absolute color="white" opacity="0.7">
      <v-progress-circular indeterminate color="#E20809" />
    </v-overlay>

    <v-timeline dense>
      <v-slide-x-reverse-transition group>
        <v-timeline-item
          v-for="comment in comments"
          :key="comment.id"
          :color="comment.user ? getUserColor(comment.user?.id) : '#FFFFFF'"
        >
          <template #icon>
            <img
              v-if="!comment.user"
              src="@/assets/img/logo_iusa.png"
              height="20"
              draggable="false"
              alt="Sistema"
            />
            <span v-else class="white--text">
              {{ getNameInitials(comment.user?.name) }}
            </span>
          </template>

          <event-history-comment-editor
            v-if="editingId === comment.id"
            :value="comment.comment"
            :initial-images="comment.images"
            is-editing
            @submit="onUpdateComment(comment.id, $event)"
            @cancel="editingId = null"
            @openGallery="onOpenImageGallery"
          />

          <v-card
            v-else
            class="d-flex flex-column pa-3 rounded"
            :elevation="0"
            :style="{
              gap: '8px',
              backgroundColor: '#FFFFFF',
            }"
          >
            <div class="d-flex align-start justify-space-between">
              <p class="ma-0 text-left text-subtitle-2 font-weight-normal">
                <span>{{ formatDate(comment.createdAt) }}</span>
                <br />
                <span class="font-weight-light">
                  {{ comment.user?.name ?? 'IUSA En Línea' }}
                </span>
                <br />
                <span class="font-weight-light">
                  {{ comment.user?.role?.label }}
                </span>
              </p>

              <v-menu v-if="comment.user?.id === user?.id" offset-y left>
                <template #activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="editingId = comment.id">
                    <v-list-item-title>Modificar</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="onDeleteComment(comment.id)">
                    <v-list-item-title>Eliminar</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div>
              <p class="ma-0 text-left" :style="{ whiteSpace: 'pre-wrap' }">
                {{ comment.comment }}
              </p>

              <v-slide-x-reverse-transition
                group
                class="d-flex flex-wrap mt-2"
                :style="{ gap: '8px' }"
              >
                <div
                  v-for="(image, index) in comment.images"
                  :key="image.id"
                  :style="{ position: 'relative' }"
                >
                  <img
                    height="50"
                    width="50"
                    :src="image.thumb"
                    :style="{
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }"
                    @click="
                      onOpenImageGallery({
                        images: galleryImages(comment),
                        index,
                      })
                    "
                  />
                  <v-btn
                    v-if="editingId === comment.id"
                    icon
                    x-small
                    :style="{
                      backgroundColor: '#E20809 !important',
                      position: 'absolute',
                      right: '-8px',
                      top: '-8px',
                    }"
                    @click.stop="removeImage(index)"
                  >
                    <v-icon x-small color="#FFFFFF">mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-slide-x-reverse-transition>
            </div>
          </v-card>
        </v-timeline-item>
      </v-slide-x-reverse-transition>

      <v-divider />

      <v-timeline-item :color="myColor">
        <template #icon>
          <span class="white--text">{{ getNameInitials(user?.name) }}</span>
        </template>
        <event-history-comment-editor
          ref="newCommentEditor"
          @submit="onCreateComment"
          @openGallery="onOpenImageGallery"
        />
      </v-timeline-item>
    </v-timeline>
  </div>
</template>

<script>
  import EventHistoryCommentEditor from './EventHistoryCommentEditor.vue';
  import { getNameInitials, getColorHsl } from '@/utilities/helpers.js';
  import DateFormats from '@/core/constants/DateFormats.js';
  import { eventBus } from '@/main';

  import { mapActions, mapGetters } from 'vuex';
  import moment from 'moment';

  export default {
    name: 'EventHistoryComments',
    components: { EventHistoryCommentEditor },
    props: {
      event: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      isLoading: false,
      editingId: null,
      comments: [],
    }),
    computed: {
      ...mapGetters('auth', ['user']),
      userColors() {
        const map = {};
        this.comments.forEach((c) => {
          const id = c.user?.id;
          if (id && !map[id]) {
            map[id] = getColorHsl(id);
          }
        });

        if (this.user?.id) {
          map[this.user.id] = '#E20809';
        }

        return map;
      },
      myColor() {
        return this.userColors[this.user?.id] ?? '#E20809';
      },
    },
    watch: {
      event: {
        immediate: true,
        handler(event) {
          if (!event) return;

          this.comments = event.comments;
        },
      },
    },
    methods: {
      ...mapActions('schedule', [
        'createEventComment',
        'updateEventComment',
        'deleteEventComment',
      ]),
      getNameInitials,
      getUserColor(userId) {
        return this.userColors[userId] ?? '#E20809';
      },
      formatDate(date) {
        return moment(date).format(DateFormats.DISPLAY_DATETIME_AMPM);
      },
      galleryImages(comment) {
        return (
          comment?.images?.map((img) => ({
            title: comment.user?.name,
            src: img.url,
          })) ?? []
        );
      },
      onOpenImageGallery({ images, index }) {
        eventBus.$emit('scheduleOpenImageGallery', { images, index });
      },
      async onCreateComment({ text, newFiles }) {
        if (this.readOnly) return;

        this.isLoading = true;

        const payload = {
          creatorId: this.user?.id,
          comment: text,
          images: newFiles,
        };

        const { error } = await this.createEventComment({
          eventId: this.event?.parentId ?? this.event?.id,
          comment: payload,
        });

        if (error) {
          this.isLoading = false;
          this.$toast.error(error);
          return;
        }

        this.$toast.success('Comentario publicado con éxito');
        this.$refs.newCommentEditor.reset();
        this.isLoading = false;
      },
      async onUpdateComment(commentId, { text, newFiles, removedImageIds }) {
        if (this.readOnly) return;

        this.isLoading = true;

        const { error } = await this.updateEventComment({
          eventId: this.event?.parentId ?? this.event?.id,
          commentId,
          text,
          newFiles,
          removedImageIds,
        });

        if (error) {
          this.isLoading = false;
          this.$toast.error(error);
          return;
        }

        this.$toast.success('Comentario modificado con éxito');
        this.editingId = null;
        this.isLoading = false;
      },
      async onDeleteComment(commentId) {
        if (this.readOnly) return;

        this.isLoading = true;

        const { error } = await this.deleteEventComment({
          eventId: this.event?.id,
          commentId,
        });

        if (error) {
          this.isLoading = false;
          this.$toast.error(error);
          return;
        }

        this.$toast.success('Comentario eliminado con éxito');
        this.isLoading = false;
      },
    },
  };
</script>

<style scoped>
  p {
    margin: 0;
  }

  ::v-deep
    .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat)
    > .v-input__control
    > .v-input__slot {
    box-shadow: none;
  }

  ::v-deep .v-list-item__title {
    text-align: left;
    cursor: pointer;
  }
</style>
